// Centralized utilities for resolving user display names consistently across the app
// Policy:
// - Prefer Firestore profile fields: username > displayName > name > userName
// - Consider passed candidates (stored values)
// - Then auth user: displayName > email local-part
// - Do not show raw emails or 'Anonymous' in UI; final fallback is 'User'

export const isEmailLike = (str) => {
  if (!str || typeof str !== 'string') return false;
  const s = String(str).trim();
  return /@/.test(s) && /\./.test(s);
};

export const getEmailLocalPart = (email) => {
  if (!email || typeof email !== 'string') return '';
  const idx = email.indexOf('@');
  return idx > 0 ? email.slice(0, idx).trim() : '';
};

export function getDisplayName({ profile = null, authUser = null, candidates = [] } = {}) {
  const names = [];
  if (profile && typeof profile === 'object') {
    names.push(profile.username, profile.userName, profile.displayName, profile.name);
  }
  if (Array.isArray(candidates)) {
    for (const c of candidates) names.push(c);
  }
  if (authUser && typeof authUser === 'object') {
    names.push(authUser.displayName, getEmailLocalPart(authUser.email));
  }

  for (const n of names) {
    if (!n || typeof n !== 'string') continue;
    const trimmed = n.trim();
    if (!trimmed) continue;
    if (/^anonymous$/i.test(trimmed)) continue;
    if (isEmailLike(trimmed)) continue;
    return trimmed;
  }

  return 'User';
}

export function isValidPhotoUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const s = url.trim();
  if (!s) return false;
  const v = s.toLowerCase();
  if (v === 'null' || v === 'undefined') return false;
  return true;
}

/**
 * Resolves a user's photo URL with fallback priority:
 * 1. profile.photoURL
 * 2. profile.userPhotoURL
 * 3. user.photoURL
 * 4. user.userPhotoURL
 * 5. authUser.photoURL (Firebase Auth)
 * 6. Empty string
 */
export function getResolvedPhotoURL({ profile = null, user = null, authUser = null, candidates = [] } = {}) {
  const urls = [];
  if (profile) {
    urls.push(profile.photoURL, profile.userPhotoURL);
  }
  if (user) {
    urls.push(user.photoURL, user.userPhotoURL);
  }
  // Add Firebase Auth photoURL as fallback (for consistency with normalizeUserProfile)
  if (authUser && typeof authUser === 'object') {
    urls.push(authUser.photoURL);
  }
  if (Array.isArray(candidates)) {
    urls.push(...candidates);
  }
  for (const u of urls) {
    if (isValidPhotoUrl(u)) return String(u).trim();
  }
  return '';
}

export function normalizeUserProfile({ doc = null, authUser = null, prev = null } = {}) {
  const profile = doc || {};
  const uid = profile.uid || (authUser && authUser.uid) || (prev && prev.uid) || null;
  const email = profile.email || (authUser && authUser.email) || (prev && prev.email) || null;
  const displayName = getDisplayName({ profile, authUser });
  const usernameCandidate = profile.username || profile.userName || profile.displayName || profile.name || displayName || getEmailLocalPart(email || '');
  const username = typeof usernameCandidate === 'string' ? usernameCandidate.trim() : '';
  const photoURL = profile.photoURL || profile.userPhotoURL || (authUser && authUser.photoURL) || (prev && prev.photoURL) || '';
  const coverPhotoURL = profile.coverPhotoURL || (prev && prev.coverPhotoURL) || '';
  const userProfession = profile.userProfession || profile.profession || (prev && prev.userProfession) || '';
  const location = profile.location || (prev && prev.location) || '';
  const vision = profile.vision || (prev && prev.vision) || '';
  const emailPublic =
    typeof profile.emailPublic === 'boolean'
      ? profile.emailPublic
      : (prev && typeof prev.emailPublic === 'boolean' ? prev.emailPublic : false);
  const locationPublic =
    typeof profile.locationPublic === 'boolean'
      ? profile.locationPublic
      : (prev && typeof prev.locationPublic === 'boolean' ? prev.locationPublic : false);
  const following = Array.isArray(profile.following) ? profile.following : (Array.isArray(prev && prev.following) ? prev.following : []);
  const followers = Array.isArray(profile.followers) ? profile.followers : (Array.isArray(prev && prev.followers) ? prev.followers : []);
  const followingCount = typeof profile.followingCount === 'number' ? profile.followingCount : following.length;
  const followersCount = typeof profile.followersCount === 'number' ? profile.followersCount : followers.length;
  const id = profile.id || (prev && prev.id) || null;
  const lastUpdated = profile.lastUpdated || (prev && prev.lastUpdated) || new Date().toISOString();
  return {
    id,
    uid,
    email,
    username,
    displayName,
    photoURL,
    coverPhotoURL,
    userProfession,
    location,
    vision,
    emailPublic,
    locationPublic,
    following,
    followers,
    followingCount,
    followersCount,
    lastUpdated,
  };
}
